import React, { useState, useCallback, useRef, useEffect } from "react";
import { Save, CheckCircle2, ChevronRight, Info, Check } from "lucide-react";
import { COMPANY_TYPES } from "@/components/CompanySetup/constants/constant";
import { useDispatch, useSelector } from "react-redux";
import { getAccountTypeDropdownList } from "@/redux/features/accountType/accountTypeSlice";
import { useNavigate } from "react-router-dom";
import CompanyTypePicker from "../components/common/CompanyTypePicker";

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLES = ["admin", "manager", "user", "finance", "sales", "operator"];

const INITIAL_DATA = [
  {
    id: 1,
    name: "Dashboard",
    permissions: {
      admin: true,
      manager: true,
      user: false,
      finance: false,
      sales: false,
      operator: false,
    },
    children: [],
  },
  {
    id: 2,
    name: "Booking",
    permissions: {
      admin: true,
      manager: true,
      user: false,
      finance: false,
      sales: true,
      operator: false,
    },
    children: [],
  },
  {
    id: 3,
    name: "Shipment",
    permissions: {
      admin: true,
      manager: true,
      user: false,
      finance: false,
      sales: false,
      operator: true,
    },
    children: [
      {
        id: 31,
        name: "Export",
        permissions: {
          admin: true,
          manager: true,
          user: false,
          finance: false,
          sales: false,
          operator: true,
        },
        children: [
          {
            id: 311,
            name: "Load Plan",
            permissions: {
              admin: true,
              manager: true,
              user: false,
              finance: false,
              sales: false,
              operator: true,
            },
            children: [],
          },
          {
            id: 312,
            name: "Booking Confirmation",
            permissions: {
              admin: true,
              manager: false,
              user: false,
              finance: false,
              sales: false,
              operator: false,
            },
            children: [],
          },
          {
            id: 313,
            name: "BL Draft",
            permissions: {
              admin: true,
              manager: false,
              user: false,
              finance: false,
              sales: false,
              operator: false,
            },
            children: [],
          },
        ],
      },
      {
        id: 32,
        name: "Import",
        permissions: {
          admin: true,
          manager: false,
          user: false,
          finance: false,
          sales: false,
          operator: false,
        },
        children: [],
      },
    ],
  },
  {
    id: 4,
    name: "Finance",
    permissions: {
      admin: true,
      manager: true,
      user: false,
      finance: true,
      sales: false,
      operator: false,
    },
    children: [],
  },
  {
    id: 5,
    name: "Reports",
    permissions: {
      admin: true,
      manager: true,
      user: false,
      finance: true,
      sales: true,
      operator: false,
    },
    children: [],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getAllIds = (nodes, out = []) => {
  nodes.forEach((n) => {
    out.push(n.id);
    if (n.children?.length) getAllIds(n.children, out);
  });
  return out;
};

const getInitialExpanded = (nodes, state = {}) => {
  nodes.forEach((node) => {
    state[node.id] = true;
    if (node.children?.length) getInitialExpanded(node.children, state);
  });
  return state;
};

/**
 * Sets permission for a node and cascades down to all descendants.
 */
const setPermCascade = (nodes, nodeId, role, checked) =>
  nodes.map((node) => {
    if (node.id === nodeId) {
      const cascade = (children) =>
        children.map((child) => ({
          ...child,
          permissions: { ...child.permissions, [role]: checked },
          children: cascade(child.children || []),
        }));
      return {
        ...node,
        permissions: { ...node.permissions, [role]: checked },
        children: cascade(node.children || []),
      };
    }
    return {
      ...node,
      children: setPermCascade(node.children || [], nodeId, role, checked),
    };
  });

/**
 * Sets a role's permission across ALL nodes in the tree.
 */
const setAllForRole = (nodes, role, value) =>
  nodes.map((node) => ({
    ...node,
    permissions: { ...node.permissions, [role]: value },
    children: setAllForRole(node.children || [], role, value),
  }));

/**
 * Collects all permission values for a role under a node (including itself).
 */
const collectPermValues = (node, role, out = []) => {
  out.push(Boolean(node.permissions?.[role]));
  node.children?.forEach((c) => collectPermValues(c, role, out));
  return out;
};

/**
 * Returns: 'all' | 'none' | 'partial'
 */
const getCheckState = (node, role) => {
  const values = collectPermValues(node, role);
  const trueCount = values.filter(Boolean).length;
  if (trueCount === 0) return "none";
  if (trueCount === values.length) return "all";
  return "partial";
};

/**
 * Counts total checked nodes for a role across the whole tree.
 */
const countCheckedForRole = (nodes, role) => {
  let count = 0;
  const walk = (ns) =>
    ns.forEach((n) => {
      if (n.permissions?.[role]) count++;
      if (n.children?.length) walk(n.children);
    });
  walk(nodes);
  return count;
};

// ─── TriCheckbox ──────────────────────────────────────────────────────────────

/**
 * A checkbox that supports indeterminate state natively via a ref.
 */
function TriCheckbox({ checkState, onChange, className = "", size = 15 }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    ref.current.indeterminate = checkState === "partial";
    ref.current.checked = checkState === "all";
  }, [checkState]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className={className}
      style={{
        width: size,
        height: size,
        cursor: "pointer",
        accentColor: "#1D9E75",
      }}
      onChange={(e) => onChange(e.target.checked)}
    />
  );
}

// ─── TreeNode ─────────────────────────────────────────────────────────────────

// Level 0 = Base Navigation  → teal tint
// Level 1 = Navigation       → blue tint
// Level 2 = Sub Navigation   → amber/warm tint
const LEVEL_STYLES = {
  0: {
    row: "bg-teal-50 dark:bg-teal-950/40 border-l-[3px] border-l-teal-500",
    label: "text-sm font-semibold text-teal-900 dark:text-teal-100",
    cell: "bg-teal-50 dark:bg-teal-950/40",
    badge: "bg-teal-100 dark:bg-teal-900/60 text-teal-700 dark:text-teal-300",
    badgeLabel: "Base Nav",
  },
  1: {
    row: "bg-blue-50 dark:bg-blue-950/30 border-l-[3px] border-l-blue-400 dark:border-l-blue-600",
    label: "text-sm font-medium text-blue-900 dark:text-blue-200",
    cell: "bg-blue-50 dark:bg-blue-950/30",
    badge: "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300",
    badgeLabel: "Nav",
  },
  2: {
    row: "bg-amber-50/60 dark:bg-amber-950/20 border-l-[3px] border-l-amber-300 dark:border-l-amber-700",
    label: "text-xs font-normal text-amber-900 dark:text-amber-200",
    cell: "bg-amber-50/60 dark:bg-amber-950/20",
    badge:
      "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300",
    badgeLabel: "Sub Nav",
  },
};

function TreeNode({
  node,
  level,
  roles,
  expandedNodes,
  toggleExpand,
  onPermissionChange,
}) {
  const hasChildren = node.children?.length > 0;
  const isExpanded = expandedNodes[node.id];
  const styles = LEVEL_STYLES[level] ?? LEVEL_STYLES[2];

  const COL_WIDTH = 96;
  const NAV_WIDTH = 280;

  return (
    <>
      {/* Row */}
      <div
        className={`
          grid items-center
          border-b border-slate-100 dark:border-slate-800
          brightness-[0.97] hover:brightness-100
          transition-all duration-100
          ${styles.row}
        `}
        style={{
          gridTemplateColumns: `${NAV_WIDTH}px repeat(${roles.length}, ${COL_WIDTH}px)`,
        }}
      >
        {/* Nav label cell — sticky */}
        <div
          className={`
            sticky left-0 z-10 flex items-center gap-1.5
            py-2.5 pr-3
            ${styles.cell}
            border-r border-slate-100 dark:border-slate-800
          `}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => hasChildren && toggleExpand(node.id)}
        >
          {hasChildren ? (
            <ChevronRight
              size={14}
              className={`shrink-0 text-slate-400 transition-transform duration-150 ${isExpanded ? "rotate-90" : ""}`}
            />
          ) : (
            <span className="w-3.5 shrink-0" />
          )}
          <span
            className={`${styles.label} truncate ${hasChildren ? "cursor-pointer select-none" : ""}`}
            title={node.name}
          >
            {node.name}
          </span>
          {level === 0 && (
            <span
              className={`ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${styles.badge}`}
            >
              Base
            </span>
          )}
          {level === 1 && (
            <span
              className={`ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${styles.badge}`}
            >
              Nav
            </span>
          )}
          {level === 2 && (
            <span
              className={`ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${styles.badge}`}
            >
              Sub
            </span>
          )}
        </div>

        {/* Permission checkboxes */}
        {roles.map((role) => {
          const checkState = getCheckState(node, role);
          return (
            <div key={role} className="flex justify-center items-center py-2.5">
              <TriCheckbox
                checkState={checkState}
                onChange={(checked) =>
                  onPermissionChange(node.id, role, checked)
                }
              />
            </div>
          );
        })}
      </div>

      {/* Children */}
      {isExpanded &&
        node.children?.map((child) => (
          <TreeNode
            key={child.id}
            node={child}
            level={level + 1}
            roles={roles}
            expandedNodes={expandedNodes}
            toggleExpand={toggleExpand}
            onPermissionChange={onPermissionChange}
          />
        ))}
    </>
  );
}

// ─── CompanyPicker ────────────────────────────────────────────────────────────

// function CompanyPicker({ onSelect }) {
//   return (
//     <div className="space-y-5">
//       <div>
//         <h2 className="text-base font-semibold text-slate-900 dark:text-white">
//           Select Company Type
//         </h2>
//         <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
//           Configure navigation visibility per role for the selected company
//           type.
//         </p>
//       </div>

//       <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
//         {COMPANY_TYPES.map((type) => (
//           <button
//             key={type.id}
//             onClick={() => onSelect(type.id)}
//             className="
//               group text-left
//               rounded-xl border border-slate-200 dark:border-slate-700
//               bg-white dark:bg-slate-800
//               p-4 hover:border-teal-400 dark:hover:border-teal-600
//               hover:shadow-sm transition-all duration-150
//               focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500
//             "
//           >
//             <div
//               className={`
//                 h-10 w-10 rounded-lg
//                 bg-gradient-to-br ${type.color}
//                 flex items-center justify-center mb-3
//               `}
//             >
//               <type.icon className="h-5 w-5 text-white" />
//             </div>
//             <div className="font-semibold text-sm text-slate-900 dark:text-white group-hover:text-teal-700 dark:group-hover:text-teal-400 transition-colors">
//               {type.name}
//             </div>
//             <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
//               {type.description}
//             </div>
//           </button>
//         ))}
//       </div>
//     </div>
//   );
// }

// ─── NavVisibilityPage ────────────────────────────────────────────────────────

export default function NavVisibilityPage() {
  const dispatch = useDispatch();
  const accountTypes = useSelector((state) => state.accountType.accountTypes);

  const [selectedCompanyTypeId, setSelectedCompanyTypeId] = useState(null);
  const [treeData, setTreeData] = useState(INITIAL_DATA);
  const [expandedNodes, setExpandedNodes] = useState(() =>
    getInitialExpanded(INITIAL_DATA),
  );
  const [savedState, setSavedState] = useState("idle"); // 'idle' | 'saving' | 'saved'

  const totalNodes = getAllIds(treeData).length;

  useEffect(() => {
    if (!accountTypes?.length) {
      dispatch(getAccountTypeDropdownList());
    }
  }, [dispatch, accountTypes?.length]);

  const toggleExpand = useCallback((id) => {
    setExpandedNodes((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handlePermissionChange = useCallback((nodeId, role, checked) => {
    setTreeData((prev) => setPermCascade(prev, nodeId, role, checked));
  }, []);

  const handleColToggle = useCallback((role, checked) => {
    setTreeData((prev) => setAllForRole(prev, role, checked));
  }, []);

  const handleSave = async () => {
    setSavedState("saving");
    // Simulate async dispatch — replace with your actual thunk:
    // await dispatch(saveNavigationVisibility({ companyTypeId: selectedCompanyTypeId, data: treeData }))
    await new Promise((r) => setTimeout(r, 800));
    setSavedState("saved");
    setTimeout(() => setSavedState("idle"), 2500);
    console.log(
      "[NavVisibility] Saved:",
      JSON.stringify(
        { companyTypeId: selectedCompanyTypeId, data: treeData },
        null,
        2,
      ),
    );
  };

  const selectedType = COMPANY_TYPES.find(
    (x) => x.id === selectedCompanyTypeId,
  );

  const ROLE_COL_WIDTH = 96;
  const NAV_COL_WIDTH = 280;

  if (!selectedCompanyTypeId) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
        <CompanyTypePicker onSelect={setSelectedCompanyTypeId} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 space-y-4">
      {/* ── Selected type bar ── */}
      <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
        <div className="flex items-center gap-3">
          <div
            className={`h-9 w-9 rounded-lg bg-gradient-to-br ${selectedType.color} flex items-center justify-center`}
          >
            <selectedType.icon className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-900 dark:text-white">
                {selectedType.name}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 dark:bg-teal-900/30 px-2 py-0.5 text-xs font-medium text-teal-700 dark:text-teal-400">
                <CheckCircle2 size={10} />
                Active
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {totalNodes} navigation items · {ROLES.length} roles
            </p>
          </div>
        </div>
        <button
          onClick={() => setSelectedCompanyTypeId(null)}
          className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium transition-colors px-3 py-1.5 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20"
        >
          Change
        </button>
      </div>

      {/* ── Level legend ── */}
      <div className="flex items-center gap-3 text-xs">
        <span className="text-slate-400 dark:text-slate-500 font-medium">
          Levels:
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-50 dark:bg-teal-950/50 border border-teal-200 dark:border-teal-800 px-2.5 py-1 text-teal-700 dark:text-teal-300 font-medium">
          <span className="w-2 h-2 rounded-full bg-teal-500 inline-block" />
          Base Navigation
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 px-2.5 py-1 text-blue-700 dark:text-blue-300 font-medium">
          <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />
          Navigation
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-2.5 py-1 text-amber-700 dark:text-amber-300 font-medium">
          <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
          Sub Navigation
        </span>
      </div>

      {/* ── Permission table ── */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="overflow-auto max-h-[68vh] scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700">
          {/* Sticky header */}
          <div
            className="sticky top-0 z-30 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm"
            style={{
              display: "grid",
              gridTemplateColumns: `${NAV_COL_WIDTH}px repeat(${ROLES.length}, ${ROLE_COL_WIDTH}px)`,
            }}
          >
            {/* Nav column header */}
            <div className="sticky left-0 z-40 bg-white dark:bg-slate-900 px-3 py-3 border-r border-slate-100 dark:border-slate-800 flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Navigation
              </span>
            </div>

            {/* Role column headers */}
            {ROLES.map((role) => {
              const checked = countCheckedForRole(treeData, role);
              const pct = Math.round((checked / totalNodes) * 100);
              const colState =
                checked === 0
                  ? "none"
                  : checked === totalNodes
                    ? "all"
                    : "partial";

              return (
                <div
                  key={role}
                  className="flex flex-col items-center justify-center gap-1 py-2 px-1 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                >
                  <TriCheckbox
                    checkState={colState}
                    size={14}
                    onChange={(val) => handleColToggle(role, val)}
                  />
                  <span className="text-xs font-semibold capitalize text-slate-700 dark:text-slate-300 leading-none">
                    {role}
                  </span>
                  <span
                    className={`text-[10px] font-medium leading-none tabular-nums ${
                      pct === 100
                        ? "text-teal-600 dark:text-teal-400"
                        : pct === 0
                          ? "text-slate-400"
                          : "text-amber-500"
                    }`}
                  >
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>

          {/* Tree rows */}
          {treeData.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              level={0}
              roles={ROLES}
              expandedNodes={expandedNodes}
              toggleExpand={toggleExpand}
              onPermissionChange={handlePermissionChange}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/30">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <Info size={12} />
            <span>Toggling a parent updates all its children</span>
          </div>

          <button
            onClick={handleSave}
            disabled={savedState === "saving"}
            className={`
              flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white
              transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500
              ${
                savedState === "saved"
                  ? "bg-teal-700 dark:bg-teal-800"
                  : "bg-teal-500 hover:bg-teal-600 active:scale-[0.98]"
              }
              disabled:opacity-60 disabled:cursor-not-allowed
            `}
          >
            {savedState === "saved" ? (
              <>
                <Check size={14} />
                Saved
              </>
            ) : savedState === "saving" ? (
              <>
                <svg
                  className="animate-spin h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Saving…
              </>
            ) : (
              <>
                <Save size={14} />
                Save Visibility
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
